import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminHeaders, apiUrl, setAdminToken } from "../../utils/api";
import styles from "./Admin.module.css";

const APP_STATUSES = ["New", "Shortlisted", "Rejected", "Closed"];
const CONTACT_STATUSES = ["Pending reply", "Responded", "Closed"];

const statusClass = (status) => {
  const key = String(status || "").toLowerCase();
  if (key.includes("short") || key.includes("respond")) return styles.statusGood;
  if (key.includes("reject")) return styles.statusBad;
  if (key === "closed") return styles.statusMuted;
  return styles.statusNew;
};

const StatusSelect = ({ value, options, onChange }) => (
  <select
    className={`${styles.statusSelect} ${statusClass(value)}`}
    value={value}
    onClick={(event) => event.stopPropagation()}
    onChange={(event) => onChange(event.target.value)}
  >
    {options.map((option) => (
      <option key={option} value={option}>
        {option}
      </option>
    ))}
  </select>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [section, setSection] = useState("applications");
  const [applications, setApplications] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [appFilter, setAppFilter] = useState("All");
  const [contactFilter, setContactFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const handleUnauthorized = useCallback(() => {
    setAdminToken("");
    navigate("/admin/login", { replace: true });
  }, [navigate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [appsRes, contactsRes] = await Promise.all([
        fetch(apiUrl("/api/admin/applications"), { headers: adminHeaders() }),
        fetch(apiUrl("/api/admin/contacts"), { headers: adminHeaders() }),
      ]);
      if (appsRes.status === 401 || contactsRes.status === 401) {
        handleUnauthorized();
        return;
      }
      const appsData = await appsRes.json();
      const contactsData = await contactsRes.json();
      if (!appsRes.ok) throw new Error(appsData.error || "Could not load applications.");
      if (!contactsRes.ok) throw new Error(contactsData.error || "Could not load contacts.");
      setApplications(appsData);
      setContacts(contactsData);
    } catch (err) {
      setError(err.message || "Could not load dashboard data.");
    } finally {
      setLoading(false);
    }
  }, [handleUnauthorized]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const logout = () => {
    setAdminToken("");
    navigate("/admin/login", { replace: true });
  };

  const patchStatus = async (path, status) => {
    const response = await fetch(apiUrl(path), {
      method: "PATCH",
      headers: {
        ...adminHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ status }),
    });
    if (response.status === 401) {
      handleUnauthorized();
      return null;
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || "Could not update status.");
    }
    return data;
  };

  const updateAppStatus = async (id, status) => {
    try {
      const updated = await patchStatus(`/api/admin/applications/${id}/status`, status);
      if (!updated) return;
      setApplications((prev) => prev.map((item) => (item.id === id ? updated : item)));
      setSelectedApp((prev) => (prev && prev.id === id ? updated : prev));
    } catch (err) {
      setError(err.message || "Could not update application status.");
    }
  };

  const updateContactStatus = async (id, status) => {
    try {
      const updated = await patchStatus(`/api/admin/contacts/${id}/status`, status);
      if (!updated) return;
      setContacts((prev) => prev.map((item) => (item.id === id ? updated : item)));
      setSelectedContact((prev) => (prev && prev.id === id ? updated : prev));
    } catch (err) {
      setError(err.message || "Could not update contact status.");
    }
  };

  const downloadCv = async (application) => {
    try {
      const response = await fetch(
        apiUrl(`/api/admin/applications/${application.id}/cv`),
        { headers: adminHeaders() }
      );
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }
      if (!response.ok) {
        throw new Error("CV could not be downloaded.");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = application.cv?.originalName || `${application.id}-cv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || "CV could not be downloaded.");
    }
  };

  const formatDate = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleString();
  };

  const visibleApps = applications.filter(
    (item) => appFilter === "All" || (item.status || "New") === appFilter
  );
  const visibleContacts = contacts.filter(
    (item) => contactFilter === "All" || (item.status || "Pending reply") === contactFilter
  );

  return (
    <div className={styles.shell}>
      <aside className={styles.sidebar}>
        <div>
          <p className={styles.brand}>Encycdata</p>
          <h1>Admin</h1>
        </div>
        <nav>
          <button
            type="button"
            className={section === "applications" ? styles.active : ""}
            onClick={() => setSection("applications")}
          >
            Job applications
            <span>{applications.length}</span>
          </button>
          <button
            type="button"
            className={section === "contacts" ? styles.active : ""}
            onClick={() => setSection("contacts")}
          >
            Contact forms
            <span>{contacts.length}</span>
          </button>
        </nav>
        <button type="button" className={styles.logout} onClick={logout}>
          Log out
        </button>
      </aside>
      <main className={styles.content}>
        <div className={styles.contentHead}>
          <div>
            <h2>{section === "applications" ? "Job applications" : "Contact forms"}</h2>
            <p>Update each submission as you review it. Changes are saved to the local JSON files.</p>
          </div>
          <div className={styles.headActions}>
            <select
              className={styles.filterSelect}
              value={section === "applications" ? appFilter : contactFilter}
              onChange={(event) =>
                section === "applications"
                  ? setAppFilter(event.target.value)
                  : setContactFilter(event.target.value)
              }
            >
              <option value="All">All statuses</option>
              {(section === "applications" ? APP_STATUSES : CONTACT_STATUSES).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <button type="button" className={styles.refresh} onClick={loadData}>
              Refresh
            </button>
          </div>
        </div>
        {error ? <p className={styles.error}>{error}</p> : null}
        {loading ? <p>Loading submissions...</p> : null}

        {!loading && section === "applications" && (
          <div className={styles.tableWrap}>
            <table>
              <thead>
                <tr>
                  <th>Submitted</th>
                  <th>Job ID</th>
                  <th>Role</th>
                  <th>Applicant</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibleApps.length === 0 ? (
                  <tr>
                    <td colSpan="7">No job applications in this view.</td>
                  </tr>
                ) : (
                  visibleApps.map((app) => (
                    <tr key={app.id}>
                      <td>{formatDate(app.submittedAt)}</td>
                      <td>{app.jobId}</td>
                      <td>{app.jobTitle}</td>
                      <td>
                        {app.firstName} {app.lastName}
                      </td>
                      <td>{app.email}</td>
                      <td>
                        <StatusSelect
                          value={app.status || "New"}
                          options={APP_STATUSES}
                          onChange={(status) => updateAppStatus(app.id, status)}
                        />
                      </td>
                      <td>
                        <button type="button" onClick={() => setSelectedApp(app)}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && section === "contacts" && (
          <div className={styles.tableWrap}>
            <table>
              <thead>
                <tr>
                  <th>Submitted</th>
                  <th>Name</th>
                  <th>Enquiry</th>
                  <th>Email</th>
                  <th>Company</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibleContacts.length === 0 ? (
                  <tr>
                    <td colSpan="7">No contact forms in this view.</td>
                  </tr>
                ) : (
                  visibleContacts.map((item) => (
                    <tr key={item.id}>
                      <td>{formatDate(item.submittedAt)}</td>
                      <td>{item.name}</td>
                      <td>{item.inquiryType || "—"}</td>
                      <td>{item.email}</td>
                      <td>{item.company || "—"}</td>
                      <td>
                        <StatusSelect
                          value={item.status || "Pending reply"}
                          options={CONTACT_STATUSES}
                          onChange={(status) => updateContactStatus(item.id, status)}
                        />
                      </td>
                      <td>
                        <button type="button" onClick={() => setSelectedContact(item)}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {selectedApp ? (
        <div className={styles.drawerBackdrop} onClick={() => setSelectedApp(null)}>
          <aside className={styles.drawer} onClick={(event) => event.stopPropagation()}>
            <button type="button" className={styles.close} onClick={() => setSelectedApp(null)}>
              Close
            </button>
            <p className={styles.drawerEyebrow}>{selectedApp.jobId}</p>
            <h3>{selectedApp.jobTitle}</h3>
            <label className={styles.statusLabel}>Application status</label>
            <StatusSelect
              value={selectedApp.status || "New"}
              options={APP_STATUSES}
              onChange={(status) => updateAppStatus(selectedApp.id, status)}
            />
            <dl>
              <div>
                <dt>Name</dt>
                <dd>
                  {selectedApp.firstName} {selectedApp.lastName}
                </dd>
              </div>
              <div>
                <dt>Contact</dt>
                <dd>{selectedApp.phone}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>{selectedApp.email}</dd>
              </div>
              <div>
                <dt>Experience</dt>
                <dd>{selectedApp.experience}</dd>
              </div>
              <div>
                <dt>Covering letter</dt>
                <dd className={styles.letter}>{selectedApp.coveringLetter}</dd>
              </div>
            </dl>
            {selectedApp.cv ? (
              <button type="button" className={styles.cvButton} onClick={() => downloadCv(selectedApp)}>
                Download CV ({selectedApp.cv.originalName})
              </button>
            ) : (
              <p>No CV uploaded.</p>
            )}
          </aside>
        </div>
      ) : null}

      {selectedContact ? (
        <div className={styles.drawerBackdrop} onClick={() => setSelectedContact(null)}>
          <aside className={styles.drawer} onClick={(event) => event.stopPropagation()}>
            <button type="button" className={styles.close} onClick={() => setSelectedContact(null)}>
              Close
            </button>
            <p className={styles.drawerEyebrow}>Contact form</p>
            <h3>{selectedContact.name}</h3>
            <label className={styles.statusLabel}>Request status</label>
            <StatusSelect
              value={selectedContact.status || "Pending reply"}
              options={CONTACT_STATUSES}
              onChange={(status) => updateContactStatus(selectedContact.id, status)}
            />
            <dl>
              <div>
                <dt>Enquiry type</dt>
                <dd>{selectedContact.inquiryType || "—"}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>{selectedContact.email}</dd>
              </div>
              <div>
                <dt>Phone</dt>
                <dd>{selectedContact.phone}</dd>
              </div>
              <div>
                <dt>Company</dt>
                <dd>{selectedContact.company || "—"}</dd>
              </div>
              <div>
                <dt>Message</dt>
                <dd className={styles.letter}>{selectedContact.message}</dd>
              </div>
              <div>
                <dt>Submitted</dt>
                <dd>{formatDate(selectedContact.submittedAt)}</dd>
              </div>
            </dl>
          </aside>
        </div>
      ) : null}
    </div>
  );
};

export default AdminDashboard;
